import React, { useEffect, useRef, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { IAuthorProfile, IUserSearchResult } from '../../core/interfaces'
import {
    createAuthorProfile,
    updateAuthorProfile,
    deleteAuthorProfile,
    searchUsers,
} from '../../services/blog-author-profiles'
import { uploadImage } from '../../services/upload-image'
import { MessageBox } from '../../core/components/MessageBox'
import { DeleteConfirmationModal } from '../../core/components/DeleteConfirmationModal'
import { UUID } from 'node:crypto'

interface Props {
    show: boolean
    onHide: () => void
    editProfile?: IAuthorProfile
    onSaved: () => void
}

const EMPTY: IAuthorProfile = {
    authorName: '',
    subheading: '',
    coverImageUrl: '',
    userId: undefined,
    userEmail: '',
    userDisplayName: '',
}

export const BlogAuthorProfileModal: React.FC<Props> = ({ show, onHide, editProfile, onSaved }) => {
    const [form, setForm] = useState<IAuthorProfile>(EMPTY)
    const [userQuery, setUserQuery] = useState('')
    const [userResults, setUserResults] = useState<IUserSearchResult[]>([])
    const [isSearchingUsers, setIsSearchingUsers] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const [showMessageBox, setShowMessageBox] = useState(false)
    const [message, setMessage] = useState('')
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const searchTimer = useRef<NodeJS.Timeout | null>(null)

    const isEdit = !!editProfile

    useEffect(() => {
        if (!show) return
        if (editProfile) {
            setForm({ ...editProfile })
            setUserQuery(editProfile.userEmail ?? '')
        } else {
            setForm(EMPTY)
            setUserQuery('')
        }
        setUserResults([])
    }, [show, editProfile])

    const handleField =
        (field: keyof IAuthorProfile) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setForm((prev) => ({ ...prev, [field]: e.target.value }))
        }

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const result = await uploadImage(
            file,
            'blog',
            setIsUploading,
            setIsError,
            setShowMessageBox,
            setMessage,
        )
        if (result.photoUrl) {
            setForm((prev) => ({ ...prev, coverImageUrl: result.photoUrl }))
        }
    }

    const handleUserQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const q = e.target.value
        setUserQuery(q)

        if (!q.trim()) {
            setUserResults([])
            setForm((prev) => ({ ...prev, userId: undefined, userEmail: '', userDisplayName: '' }))
            return
        }

        if (searchTimer.current) clearTimeout(searchTimer.current)
        searchTimer.current = setTimeout(async () => {
            setIsSearchingUsers(true)
            try {
                const { data } = await searchUsers(q.trim())
                setUserResults(data)
            } catch {
                setUserResults([])
            } finally {
                setIsSearchingUsers(false)
            }
        }, 400)
    }

    const selectUser = (user: IUserSearchResult) => {
        setForm((prev) => ({
            ...prev,
            userId: user.id,
            userEmail: user.email,
            userDisplayName: user.displayName,
        }))
        setUserQuery(`${user.displayName} (${user.email})`)
        setUserResults([])
    }

    const clearUser = () => {
        setForm((prev) => ({ ...prev, userId: undefined, userEmail: '', userDisplayName: '' }))
        setUserQuery('')
        setUserResults([])
    }

    const handleSave = async () => {
        if (!form.authorName?.trim()) {
            setIsError(true)
            setMessage('Author name is required.')
            setShowMessageBox(true)
            return
        }
        try {
            setIsLoading(true)
            if (isEdit && editProfile?.id) {
                await updateAuthorProfile(editProfile.id as UUID, form)
            } else {
                await createAuthorProfile(form)
            }
            setIsError(false)
            setMessage(isEdit ? 'Author profile updated.' : 'Author profile created.')
            setShowMessageBox(true)
            onSaved()
            handleClose()
        } catch (e: any) {
            setIsError(true)
            setMessage(e?.message ?? 'An error occurred.')
            setShowMessageBox(true)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!editProfile?.id) return
        try {
            await deleteAuthorProfile(editProfile.id as UUID)
            onSaved()
            handleClose()
        } catch (e: any) {
            setIsError(true)
            setMessage(e?.message ?? 'Delete failed.')
            setShowMessageBox(true)
        }
        setShowDeleteConfirm(false)
    }

    const handleClose = () => {
        setForm(EMPTY)
        setUserQuery('')
        setUserResults([])
        onHide()
    }

    return (
        <>
            <Modal show={show} onHide={handleClose} size="lg" backdrop="static" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isEdit ? `Edit: ${editProfile?.authorName}` : 'New Blog Author'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form className="d-flex flex-column gap-3">
                        {/* Author Name */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Author Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.authorName}
                                onChange={handleField('authorName')}
                                placeholder="e.g. Maria Popescu"
                            />
                        </Form.Group>

                        {/* Subheading */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Subheading</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.subheading ?? ''}
                                onChange={handleField('subheading')}
                                placeholder="e.g. Psiholog și mamă a 2 copii"
                            />
                        </Form.Group>

                        {/* Avatar */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Avatar Image</Form.Label>
                            <div className="d-flex align-items-center gap-3">
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    disabled={isUploading}
                                    style={{ maxWidth: 320 }}
                                />
                                {isUploading && <span className="text-muted">Uploading...</span>}
                            </div>
                            {form.coverImageUrl && (
                                <div className="mt-2 d-flex align-items-center gap-3">
                                    <img
                                        src={form.coverImageUrl}
                                        alt="Avatar"
                                        style={{
                                            width: 64,
                                            height: 64,
                                            objectFit: 'cover',
                                            borderRadius: '50%',
                                            border: '2px solid #ddd',
                                        }}
                                    />
                                    <Form.Text className="text-muted">{form.coverImageUrl}</Form.Text>
                                </div>
                            )}
                        </Form.Group>

                        {/* Linked User */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Linked User (optional)</Form.Label>
                            <Form.Text className="d-block text-muted mb-1">
                                Search by name or email to link this author to a platform user account.
                            </Form.Text>
                            <div className="position-relative">
                                <Form.Control
                                    type="text"
                                    value={userQuery}
                                    onChange={handleUserQueryChange}
                                    placeholder="Search users by name or email..."
                                    autoComplete="off"
                                />
                                {isSearchingUsers && (
                                    <div className="position-absolute end-0 top-50 translate-middle-y pe-3">
                                        <span className="text-muted" style={{ fontSize: 12 }}>Searching...</span>
                                    </div>
                                )}
                                {userResults.length > 0 && (
                                    <div
                                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                                        style={{ zIndex: 1050, top: '100%', maxHeight: 220, overflowY: 'auto' }}
                                    >
                                        {userResults.map((u) => (
                                            <div
                                                key={u.id as string}
                                                className="px-3 py-2"
                                                style={{ cursor: 'pointer' }}
                                                onMouseDown={() => selectUser(u)}
                                                onMouseEnter={(e) =>
                                                    (e.currentTarget.style.backgroundColor = '#f0f0f0')
                                                }
                                                onMouseLeave={(e) =>
                                                    (e.currentTarget.style.backgroundColor = '')
                                                }
                                            >
                                                <strong>{u.displayName}</strong>{' '}
                                                <span className="text-muted">— {u.email}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {form.userId && (
                                <div className="mt-2 d-flex align-items-center gap-2">
                                    <span className="badge bg-success">
                                        Linked: {form.userDisplayName} ({form.userEmail})
                                    </span>
                                    <Button
                                        variant="outlined"
                                        size="sm"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={clearUser}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            )}
                        </Form.Group>
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    {isEdit && (
                        <Button
                            className="btn btn-danger me-auto"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            Delete
                        </Button>
                    )}
                    <Button className="btn btn-secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={isLoading || isUploading}
                    >
                        {isLoading ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <DeleteConfirmationModal
                showDeleteConfirmationModal={showDeleteConfirm}
                setDeleteConfirmationModal={setShowDeleteConfirm}
                deleteTitle="Delete Author Profile"
                deleteFunc={handleDelete}
            />

            <MessageBox
                message={message}
                setShowMessageBox={setShowMessageBox}
                showMessageBox={showMessageBox}
                isError={isError}
            />
        </>
    )
}
